import React from 'react';
import PropTypes from 'prop-types';
import { action, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';

import RowContext from './context';
import StringValue from '../fields/StringValue';
import NumberValue from '../fields/NumberValue';
import DateValue from '../fields/DateValue';
import BooleanValue from '../fields/BooleanValue';
import ObjectValue from '../fields/ObjectValue';

@observer
export default class Column extends React.Component {

	static propTypes = {
		label: PropTypes.string,
		property: PropTypes.string,
		relation: PropTypes.string,
		title: PropTypes.string,
		computed: PropTypes.func,
		sortFunc: PropTypes.func,
		type: PropTypes.string,
		className: PropTypes.string,
		style: PropTypes.object,
		padding: PropTypes.number,
		width: PropTypes.any,
		error: PropTypes.any,
		multiline: PropTypes.bool,
		saveOnChange: PropTypes.bool,
		emptyField: PropTypes.string,
		clickable: PropTypes.bool,
		onClick: PropTypes.func,
	};

	static defaultProps = {
		multiline: false,
		error: '?',
		emptyField: '-',
		padding: 10,
		__isColumn: true,
		clickable: true,
		saveOnChange: false,
	};

	/*
		Class.contextType: в this.context принимаем данные от TableRow:
		{ record, row, save, onChange }
	*/

	static contextType = RowContext;

	@observable record = null;
	@observable type = null;
	@observable promiseResult = undefined;

	componentDidMount() {
		this.init();
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		this.init();
	}

	@action init = async () => {
		const { relation, property, computed } = this.props;
		const { record } = this.context;
		if (relation) {
			if (record.MODEL.RELATIONS[relation]) {
				const model = record.MODEL.RELATIONS[relation].model;
				const propertyObj = record.MODEL.STORE[model].PROPERTIES[property];
				if (property && !propertyObj) {
					throw new Error(`Relation '${relation}' does not have a property named '${property}'`);
				}
				this.type = propertyObj ? propertyObj.type : null;
				this.foreignKey = record.MODEL.RELATIONS[relation].foreignKey;
				try {
					this.record = await record[relation];
				}
				catch(e) {
					console.warn(e);
					this.record = -1;
				}
			}
			else {
				throw new Error(`Relation '${relation}' does not exist`);
			}
		}
		else {
			if (record.MODEL && record.MODEL.PROPERTIES[property]) {
				this.type = computed ? 'computed' : property ? record.MODEL.PROPERTIES[property].type : 'function';
			}
			else {
				// console.warn('no property data', property, record.MODEL.PROPERTIES);
			}
			this.record = record;
		}
	};

	onChange = res => {
		let value = res;
		if (res && typeof res === 'object' && res.constructor.name === 'SyntheticEvent') {
			value = res.target.value;
		}
		// console.log('Column onChange', value, res);
		if (this.context.onChange) {
			this.context.onChange(this.props.property, value);
		}
		if (this.props.saveOnChange && this.context.save) this.context.save();
	};

	onBlur = () => {
		if (this.context.save) this.context.save();
	};

	onClick = (e, record, column, row) => {
		if (this.props.onClick) {
			e.stopPropagation();
			this.props.onClick(record, column, row);
		}
		else if (this.props.clickable === false || this.context.clickable === false) {
			e.stopPropagation();
		}
	};

	render() {
		const {
			property,
			computed,
			relation,
			column,
			children,
			title,
			emptyField,
			width,
			style,
			label,
		} = this.props;

		const { row, debug } = this.context;

		let className = [
			this.props.className,
			this.props.multiline ? 'multiline' : null,
			!this.props.onClick && (this.props.clickable === false || this.context.clickable === false) ? 'no-pointer' : null,
		].join(' ').trim() || undefined;

		if (!this.record) return <td className={className}>{emptyField}</td>;

		let value = null;
		if (computed) {
			value = computed(this.record, this.context.record, column, row);
		}
		else if (property) {
			if (this.record !== -1) {
				value = this.record ? this.record[property] : null
			}
			else {
				value = '?';
			}
		}
		else {
			value = undefined;
		}

		/*
			Передаём дальше пропы:
			{
			     __data: { record, property, relation, computed, column, row, label },
			     value,
			     onChange,
			     onBlur,
			}
		*/

		const sendProps = {
			__data: { record: this.record, property, relation, computed, emptyField, column, row, label, inTable: true },
			value: toJS(value),
			onChange: this.onChange,
			onBlur: this.onBlur,
		};

		let Value = StringValue;
		switch (this.type) {
			case 'Number':
				Value = NumberValue;
				break;
			case 'Date':
				Value = DateValue;
				break;
			case 'Boolean':
				Value = BooleanValue;
				break;
			case 'Object':
				Value = ObjectValue;
				break;
			default:
		}

		const child = children || <Value />;

		const _onChange = props => arg => {
			const res = props.onChange(arg);
			this.onChange(res !== undefined ? res : arg);
		};

		const processChild = child => {
			if (typeof child === 'function') {
				const res = child(this.record, this.context.record, column, row);
				if (res && typeof res.then === 'function') {
					res.then(r => this.promiseResult = r);
					return '';
				}
				return res;
			}
			else if (React.isValidElement(child)) {
				const props = { ...sendProps };
				if (child.props && child.props.onChange) {
					props.onChange = _onChange(child.props);
				}
				return React.cloneElement(child, props);
			}
			else if (child instanceof Array) {
				return child.map((c, i) => <React.Fragment key={i}>{processChild(c)}</React.Fragment>);
			}
			return child;
		};

		let content = this.promiseResult !== undefined ? this.promiseResult : processChild(child);

		const relationId = this.foreignKey ? this.context.record[this.foreignKey] : 'x';

		const type = <small className="type">
			{!computed ?
				this.type + ': ' + JSON.stringify(this.record !== -1 ? String(value).substr(0, 10) : relationId)
				:
				'computed'
			}
		</small>;

		const key = this.record.id + '-' + column;
		// console.log('key', key, content);

		return <td key={key} title={title} width={width} style={style} className={className} onClick={e => this.onClick(e, this.record, column, row)}>
			{debug && type}
			{content}
		</td>;

	}
}
